/**
 * Error Monitoring Service
 * Handles error tracking, reporting, and monitoring for the application
 * Provides structured error logging with context and user information
 */
import config from '../config';
import { analyticsService } from './analyticsService';
import { logger } from '../utils/logger';

// Error types and interfaces
interface ErrorContext {
  userId?: string;
  userAgent: string;
  url: string;
  timestamp: number;
  sessionId: string;
  buildVersion?: string;
  environment: string;
}

interface ErrorReport {
  id: string;
  message: string;
  stack?: string;
  type: 'javascript' | 'network' | 'api' | 'react' | 'custom';
  severity: 'low' | 'medium' | 'high' | 'critical';
  context: ErrorContext;
  metadata?: Record<string, any>;
  fingerprint?: string;
}

interface NetworkError {
  url: string;
  method: string;
  status: number;
  statusText: string;
  responseText?: string;
}

interface ApiError {
  endpoint: string;
  method: string;
  status: number;
  message: string;
  requestId?: string;
}

/**
 * Error Monitoring Service Class
 */
class ErrorMonitoringService {
  private isEnabled: boolean;
  private userId: string | null = null;
  private sessionId: string;
  private errorQueue: ErrorReport[] = [];
  private maxQueueSize = 50;

  constructor() {
    this.isEnabled = config.FEATURES.ENABLE_ERROR_REPORTING;
    this.sessionId = this.generateSessionId();

    if (this.isEnabled) {
      this.setupErrorHandlers();
    }
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Set up global error handlers
   */
  private setupErrorHandlers(): void {
    // Global JavaScript error handler
    window.addEventListener('error', (event) => {
      this.captureError({
        message: event.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }, 'javascript', 'high');
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError({
        message: 'Unhandled Promise Rejection',
        reason: event.reason,
        stack: event.reason?.stack,
      }, 'javascript', 'high');
    });

    // Network error monitoring (if fetch is available)
    if (typeof window.fetch === 'function') {
      this.setupNetworkMonitoring();
    }
  }

  /**
   * Set up network request monitoring
   */
  private setupNetworkMonitoring(): void {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const [resource, config] = args;
      const url = typeof resource === 'string' ? resource : (resource as Request).url;
      const method = config?.method || 'GET';

      try {
        const response = await originalFetch(...args);

        // Log failed HTTP requests
        if (!response.ok) {
          this.captureNetworkError({
            url,
            method,
            status: response.status,
            statusText: response.statusText,
          });
        }

        return response;
      } catch (error) {
        // Log network failures
        this.captureNetworkError({
          url,
          method,
          status: 0,
          statusText: 'Network Error',
          responseText: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }
    };
  }

  /**
   * Set the current user ID
   */
  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  /**
   * Create error context
   */
  private createErrorContext(): ErrorContext {
    return {
      userId: this.userId || undefined,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      buildVersion: config.APP.VERSION,
      environment: config.APP.ENVIRONMENT,
    };
  }

  /**
   * Generate error fingerprint for deduplication
   */
  private generateFingerprint(message: string, stack?: string): string {
    const content = stack || message;
    // Simple hash function for fingerprinting
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Capture a generic error
   */
  captureError(
    error: any,
    type: ErrorReport['type'] = 'custom',
    severity: ErrorReport['severity'] = 'medium',
    metadata?: Record<string, any>
  ): string {
    if (!this.isEnabled) return '';

    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const message = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : undefined;

    const errorReport: ErrorReport = {
      id: errorId,
      message,
      stack,
      type,
      severity,
      context: this.createErrorContext(),
      metadata,
      fingerprint: this.generateFingerprint(message, stack),
    };

    this.addToQueue(errorReport);
    this.logError(errorReport);

    // Send to analytics
    analyticsService.trackError({
      message,
      stack,
    });

    return errorId;
  }

  /**
   * Capture a React component error
   */
  captureReactError(error: Error, errorInfo: { componentStack: string }): string {
    return this.captureError(error, 'react', 'high', {
      componentStack: errorInfo.componentStack,
    });
  }

  /**
   * Capture a network error
   */
  captureNetworkError(networkError: NetworkError): string {
    const severity = networkError.status >= 500 ? 'high' : 'medium';

    return this.captureError(
      new Error(`Network Error: ${networkError.status} ${networkError.statusText}`),
      'network',
      severity,
      {
        url: networkError.url,
        method: networkError.method,
        status: networkError.status,
        statusText: networkError.statusText,
        responseText: networkError.responseText,
      }
    );
  }

  /**
   * Capture an API error
   */
  captureApiError(apiError: ApiError): string {
    const severity = apiError.status >= 500 ? 'high' : 'medium';

    return this.captureError(
      new Error(`API Error: ${apiError.message}`),
      'api',
      severity,
      {
        endpoint: apiError.endpoint,
        method: apiError.method,
        status: apiError.status,
        requestId: apiError.requestId,
      }
    );
  }

  /**
   * Capture a custom error with context
   */
  captureMessage(
    message: string,
    severity: ErrorReport['severity'] = 'medium',
    metadata?: Record<string, any>
  ): string {
    return this.captureError(new Error(message), 'custom', severity, metadata);
  }

  /**
   * Add error to queue
   */
  private addToQueue(errorReport: ErrorReport): void {
    this.errorQueue.push(errorReport);

    // Limit queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // Auto-flush critical errors
    if (errorReport.severity === 'critical') {
      this.flushErrors();
    }
  }

  /**
   * Log error to console (development) or send to service (production)
   */
  private logError(errorReport: ErrorReport): void {
    const logLevel = {
      low: 'log',
      medium: 'warn',
      high: 'error',
      critical: 'error',
    }[errorReport.severity] as 'log' | 'warn' | 'error';

    const log = logger.child('ERRORS');
    if (logLevel === 'error') {
      log.error('🚨 Error captured:', {
        id: errorReport.id,
        message: errorReport.message,
        type: errorReport.type,
        severity: errorReport.severity,
        context: errorReport.context,
        metadata: errorReport.metadata,
      });
    } else if (logLevel === 'warn') {
      log.warn('🚨 Error captured:', {
        id: errorReport.id,
        message: errorReport.message,
        type: errorReport.type,
        severity: errorReport.severity,
        context: errorReport.context,
        metadata: errorReport.metadata,
      });
    } else {
      log.log('🚨 Error captured:', {
        id: errorReport.id,
        message: errorReport.message,
        type: errorReport.type,
        severity: errorReport.severity,
        context: errorReport.context,
        metadata: errorReport.metadata,
      });
    }

    // In production, you would send this to your error monitoring service
    // Example: Sentry, LogRocket, Bugsnag, etc.
    if (!config.isDevelopment) {
      this.sendToErrorService(errorReport);
    }
  }

  /**
   * Send error to external monitoring service
   */
  private async sendToErrorService(errorReport: ErrorReport): Promise<void> {
    try {
      // This would integrate with your error monitoring service
      // For now, we'll just log it
      logger.child('ERRORS').log('📤 Sending error to monitoring service:', errorReport.id);

      // Example integration with a hypothetical error service:
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorReport),
      // });
    } catch (error) {
      logger.child('ERRORS').error('Failed to send error to monitoring service:', error);
    }
  }

  /**
   * Flush all queued errors
   */
  async flushErrors(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    for (const error of errors) {
      await this.sendToErrorService(error);
    }
  }

  /**
   * Get error statistics
   */
  getErrorStats(): {
    total: number;
    bySeverity: Record<string, number>;
    byType: Record<string, number>;
    recent: ErrorReport[];
  } {
    const bySeverity: Record<string, number> = {};
    const byType: Record<string, number> = {};

    this.errorQueue.forEach(error => {
      bySeverity[error.severity] = (bySeverity[error.severity] || 0) + 1;
      byType[error.type] = (byType[error.type] || 0) + 1;
    });

    return {
      total: this.errorQueue.length,
      bySeverity,
      byType,
      recent: this.errorQueue.slice(-10), // Last 10 errors
    };
  }

  /**
   * Clear error queue
   */
  clearErrors(): void {
    this.errorQueue = [];
  }

  /**
   * Enable or disable error monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Add breadcrumb for debugging context
   */
  addBreadcrumb(message: string, category: string = 'default', data?: Record<string, any>): void {
    if (!this.isEnabled) return;

    logger.child('ERRORS').debug('🍞 Breadcrumb:', { message, category, data, timestamp: Date.now() });

    // In a real implementation, you'd store breadcrumbs and include them with error reports
  }
}

// Export singleton instance
export const errorMonitoringService = new ErrorMonitoringService();
export default errorMonitoringService;